import { getAverageColorByImage, loadImageFromUrl } from '@hts/shared/utils';
import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/Banner.module.scss';

interface IBannerProps {
  src: string;
  linkTo: string;
}

interface IBannerState {
  isLoading: boolean;
  dimensions: { width: number; height: number };
  averageColor?: string;
}

const initialState: IBannerState = {
  isLoading: true,
  dimensions: { width: 0, height: 0 },
  averageColor: undefined,
};

export const Banner: React.FC<IBannerProps> = memo(({ src, linkTo }) => {
  const [state, setState] = useState<IBannerState>(initialState);

  const aspectRatio =
    state.dimensions.width > 0 && state.dimensions.height > 0
      ? `auto ${state.dimensions.width} / ${state.dimensions.height}`
      : undefined;

  useEffect(() => {
    let isMounted = true;

    const loadBanner = async () => {
      const image = await loadImageFromUrl(src, true);

      if (!image || !isMounted) {
        if (isMounted) {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
        return;
      }

      if (isMounted) {
        const averageColor = getAverageColorByImage(image);

        setState({
          isLoading: false,
          dimensions: { width: image.width, height: image.height },
          averageColor,
        });
      }
    };

    loadBanner();

    return () => {
      isMounted = false;
    };
  }, [src]);

  if (state.isLoading || !state.dimensions.width || !state.dimensions.height) {
    return null;
  }

  return (
    <div className={styles.container} style={{ background: state.averageColor }}>
      <Link to={linkTo} className={styles.imageLink}>
        <div
          className={styles.imageWrapper}
          style={{ backgroundImage: `url("${src}")`, aspectRatio }}
        />
      </Link>
    </div>
  );
});
